import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Label from '@/components/ui/forms/Label';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import useAuthentication from '@/hooks/common/useAuthentication.ts';
import useFetch from '@/hooks/common/useFetch';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
} from '@/interfaces/parent';

import CSSModule from './ResetPasswordForm.module.scss';

type FormValues = {
  password: string;
  password_confirmation: string;
};

interface Props {
  token?: string | (string | null)[] | null;
  email?: string;
  setCompleted: (completed: boolean) => void;
  setIsVerificationError: (isVerificationError: boolean) => void;
}

const ResetPasswordForm: React.FC<Props> = ({
  token,
  email,
  setCompleted,
  setIsVerificationError,
}) => {
  const intl = useIntl();
  const { isLoggedIn } = useAuthentication();

  const [formValues] = useState<FormValues>({
    password: '',
    password_confirmation: '',
  });
  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(
        intl.formatMessage(
          { id: 'forms.errors.required' },
          {
            label: 'パスワード',
          },
        ),
      )
      .min(
        PASSWORD_MIN_LENGTH,
        intl.formatMessage(
          { id: 'forms.errors.minLength' },
          {
            label: 'パスワード',
            value: PASSWORD_MIN_LENGTH,
          },
        ),
      )
      .max(
        PASSWORD_MAX_LENGTH,
        intl.formatMessage(
          { id: 'forms.errors.maxLength' },
          {
            label: 'パスワード',
            value: PASSWORD_MAX_LENGTH,
          },
        ),
      )
      .matches(
        PASSWORD_REGEXP,
        'パスワードは半角アルファベットと半角数字をそれぞれ1文字以上で入力してください。',
      )
      .notOneOf(
        [email],
        'メールアドレスとパスワードに同じ文字列は使用できません。',
      ),
    password_confirmation: Yup.string()
      .required(
        intl.formatMessage(
          { id: 'forms.errors.required' },
          {
            label: 'パスワード（確認用）',
          },
        ),
      )
      .oneOf([Yup.ref('password')], 'パスワードが一致しません。'),
  });

  const {
    fetch: resetPassword,
    isFetching,
    status,
  } = useFetch('PATCH', '/api/school/parents/password/reset');

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid, touchedFields, errorMessages },
    trigger,
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const onSubmit = async (values: FormValues) => {
    await resetPassword({
      headers: {
        'X-Token-Type': 'password_change_token',
        Authorization: `Bearer ${token as string}`,
      },
      body: values,
    });
  };

  useEffect(() => {
    setCompleted(status === 200);
  }, [status, setCompleted]);

  useEffect(() => {
    setIsVerificationError(!!(status && 400 <= status && status <= 499));
  }, [setIsVerificationError, status]);

  const inputDisabled = isSubmitting || isFetching;
  const buttonDisabled = !isDirty || !isValid || isSubmitting || isFetching;

  return (
    <form
      className={CSSModule.ResetPasswordForm}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
    >
      <FormGroup>
        <Label>新しいパスワード</Label>
        <Field>
          <PasswordInput
            {...registerWithInnerRef('password', {
              onChange: () => {
                if (touchedFields.password_confirmation) {
                  void trigger('password_confirmation');
                }
              },
            })}
            placeholder="パスワードを入力"
            disabled={inputDisabled}
          />
          <ErrorList errors={errorMessages.password} />
        </Field>
        <Field>
          <PasswordInput
            {...registerWithInnerRef('password_confirmation')}
            placeholder="パスワードを入力（確認用）"
            disabled={inputDisabled}
          />
          <ErrorList errors={errorMessages.password_confirmation} />
        </Field>
      </FormGroup>

      <ul className={CSSModule.ResetPasswordForm__Notes}>
        <li>
          <strong>＊ 最低6文字以上にしてください </strong>
        </li>
        <li>
          <strong>
            ＊ 半角アルファベットと半角数字をそれぞれ1文字以上入れてください
          </strong>
        </li>
        <li>
          ＊
          名前＋生年月日などは推測されやすいため、英数字以外の記号組み合わせを推奨します
        </li>
      </ul>

      <div className={CSSModule.ResetPasswordForm__Buttons}>
        <Button
          color="primary"
          disabled={buttonDisabled}
          type="submit"
          className={CSSModule.ResetPasswordForm__SubmitButton}
        >
          再設定
        </Button>
        <Button
          tag={Link}
          to={isLoggedIn ? '/mypage/profile' : '/login'}
          color="outline"
          className={CSSModule.ResetPasswordForm__BackToLoginButton}
        >
          {isLoggedIn ? 'マイページに戻る' : 'ログイン画面に戻る'}
        </Button>
      </div>
    </form>
  );
};

export default ResetPasswordForm;
